import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Flex, Image, Modal, Popover, Space, Typography } from 'antd'
import { Header } from 'antd/es/layout/layout'
import { LeftOutlined } from '@ant-design/icons'
import { ArrowDownIcon, DangerIcon } from 'src/assets/icons'
import ProfilePic from 'src/assets/images/profile-pic.png'
import { colorBorderDisabled, colorTextSubtle } from 'src/app/themes/primaryThemeDefault'
import { menuUrls, UrlToPageNameMapping } from 'src/app/shared/constants/menu.constants'
import {
  ROUTES_WITH_BACK_BUTTON,
  ROUTES_HIDDEN_FAVORITE_ICON,
} from 'src/app/shared/constants/app.constants'
import {
  getAllPages,
  getFavoritePages,
  updateFavoritePages,
} from 'src/app/shared/services/UserPagesService'
import { setSelectedPageKey } from 'src/app/store/common'
import { sortByOrder } from 'src/app/shared/utils'
import { getUserProfile } from 'src/app/store/auth'
import AccountMenu from './account-menu'
import Logo from 'src/assets/images/logo-help-page.png'
import './index.scss'

interface PropsHelpPageHeader {
  title?: string
  isShowBackIcon?: boolean
  onGoBack?: () => any
}

const HelpPageHeader: React.FC<PropsHelpPageHeader> = ({ title, onGoBack, isShowBackIcon = true }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const userProfile = useSelector(getUserProfile)
  const { userId, name } = userProfile
  const params = useParams()
  const { id } = params

  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false)
  const [isFavoriteConfirmationModalOpen, setIsFavoriteConfirmationModalOpen] = useState(false)
  const [isUnsaveAllFavoritePagesModalOpen, setIsUnsaveAllFavoritePagesModalOpen] = useState(false)


  const [allPagesList, setAllPagesList] = useState([])
  const [favoritePagesList, setFavoritePagesList] = useState<any>([])
  const [initialAllPagesList, setInitialAllPagesList] = useState([])
  const [initialFavoritePages, setInitialFavoritePages] = useState([])
  const [flag, setFlag] = useState(false)
  const [reget, setReget] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const pathName = useMemo(() => {
    if (id) return location.pathname?.replace(`/${id}`, '')

    return location.pathname
  }, [id, location])

  const isFavoriteIconDisplay = !ROUTES_HIDDEN_FAVORITE_ICON?.includes(pathName as menuUrls)

  useEffect(() => {
    getAllPagesData()
    if (userId) {
      getFavoritePagesData(userId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reget])

  useEffect(() => {
    dispatch(setSelectedPageKey(UrlToPageNameMapping[pathName]))
  }, [dispatch, pathName])

  const handleFavoriteConfirmationCancel = () => {
    handleDiscardUnsavedFavoriteChanges()
    setIsFavoriteConfirmationModalOpen(false)
  }


  const handleAccountMenuOpenChange = (newOpen: boolean) => {
    setIsAccountMenuOpen(newOpen)
  }

  const handleDiscardUnsavedFavoriteChanges = () => {
    setFavoritePagesList(initialFavoritePages)
    setAllPagesList(initialAllPagesList)
    setFlag((prev) => !prev)
  }

  const handleSaveFavoriteChanges = async (listItem: any) => {
    setIsLoading(true)
    await updateFavoritePages({
      userId,
      pageIdList: listItem.map((item: any) => item.id),
    })
    setReget((prev) => !prev)
    setIsFavoriteConfirmationModalOpen(false)
    setIsLoading(false)
  }

  const getAllPagesData = async () => {
    try {
      const response = await getAllPages()
      const orderResponse = sortByOrder(response?.data?.result)
      if (response?.data?.result) {
        setAllPagesList(orderResponse)
        setInitialAllPagesList(orderResponse)
      }
    } catch (err) {
      console.log(err)
    }
  }

  const getFavoritePagesData = async (userId: string) => {
    try {
      const response = await getFavoritePages(userId)
      const orderFavoriteList = sortByOrder(response?.data?.result, 'favoritePosition')
      if (response?.data?.result) {
        setFavoritePagesList(orderFavoriteList)
        setInitialFavoritePages(orderFavoriteList)
      }
    } catch (err) {
      console.log(err)
    }
  }
  const deletePages = async () => {
    setIsLoading(true)
    await updateFavoritePages({
      userId,
      pageIdList: [],
    })
    setReget((prev) => !prev)
    setIsFavoriteConfirmationModalOpen(false)
    setIsUnsaveAllFavoritePagesModalOpen(false)
    setIsLoading(false)
  }

  const nonFavoritePagesList = useMemo(() => {
    const favoritePagesIdList = favoritePagesList?.map((favItem: any) => favItem?.id)
    const data = allPagesList?.filter((item: any) => {
      return !favoritePagesIdList?.includes(item?.id)
    })

    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritePagesList, allPagesList, flag])

  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack()
    } else {
      navigate(-1)
    }
  }

  return (
    <Header
      className="help-page-header"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 500,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Flex align="center" gap={20} style={{ width: '100%' }}>
        <img onClick={() => navigate(menuUrls.helpPageManagement)} src={Logo} alt="help page logo" style={{ height: '100%', cursor: 'pointer' }} />

        <Flex justify="space-between" align="center" className="help-page-header-title-wrap" style={{ marginLeft: 50 }}>
          <Flex align="center" gap={10} style={{ borderLeft: `2px solid ${colorBorderDisabled}`, height: 26, paddingLeft: 24 }}>
            {isShowBackIcon && <LeftOutlined style={{ fontSize: 12 }} onClick={handleGoBack} />}
            <Typography.Text className="header-title">{title}</Typography.Text>
          </Flex>
        </Flex>

        <Popover
          className="help-page-header-popover"
          placement="bottomRight"
          content={<AccountMenu />}
          trigger="click"
          open={isAccountMenuOpen}
          onOpenChange={handleAccountMenuOpenChange}
        >
          <Flex className="help-page-account-wrap" align="center" gap={8}>
            <img className="help-page-profile-picture" src={ProfilePic} alt="" />
            <Typography.Text className="account-username" style={{ fontSize: '16px', paddingTop: '2px' }}>
              {name}
            </Typography.Text>
            <ArrowDownIcon />
          </Flex>
        </Popover>
      </Flex>

      <Modal
        open={isUnsaveAllFavoritePagesModalOpen}
        cancelButtonProps={{ disabled: false }}
        onCancel={() => {
          handleDiscardUnsavedFavoriteChanges()
          setIsUnsaveAllFavoritePagesModalOpen(false)
        }}
        centered
        width={480}
        footer={false}
      >
        <Flex vertical align="center" gap={12}>
          <DangerIcon />
          <Typography.Title style={{ fontSize: '20px', fontWeight: '700', margin: '0', padding: '0' }}>
            {t('OverallFunctionality:unsave_all_modal_title_text')}
          </Typography.Title>
          <Typography.Text style={{ fontSize: '14px', color: colorTextSubtle }}>
            {t('OverallFunctionality:unsave_all_modal_description_text')}
          </Typography.Text>
          <Space style={{ paddingTop: '16px' }} size={16}>
            <Button
              key="logout-modal-cancel"
              type="default"
              style={{ width: '80px', fontWeight: 600 }}
              onClick={() => {
                handleDiscardUnsavedFavoriteChanges()
                setIsUnsaveAllFavoritePagesModalOpen(false)
              }}
              ghost
            >
              {t('OverallFunctionality:no_btn')}
            </Button>
            <Button style={{ width: '80px' }} type="primary" onClick={deletePages} disabled={isLoading}>
              {t('OverallFunctionality:yes_btn')}
            </Button>
          </Space>
        </Flex>
      </Modal>
    </Header>
  )
}

export default HelpPageHeader
